import { apiClient } from './api'

export interface ProjectReportItem {
  id: number
  project_id: number
  report_type: string
  title: string
  description: string | null
  owner_id: number | null
  status: string
  generated_at: string | null
  report_data: Record<string, unknown> | null
  file_path: string | null
}

export interface ProjectReportCreate {
  report_type: string
  title: string
  description?: string
  status?: string
  report_data?: Record<string, unknown>
  metadata?: Record<string, unknown>
}

export interface ProjectReportExport {
  report_id: number
  project_id: number
  format: string
  status: string
  title: string
  report_type: string
  generated_at: string | null
  data: Record<string, unknown>
}

export const projectReportsService = {
  async list(
    projectId: number,
    params?: { report_type?: string; status?: string }
  ): Promise<ProjectReportItem[]> {
    const { data } = await apiClient.get<ProjectReportItem[]>(
      `/api/v1/projects/${projectId}/reports/`,
      { params }
    )
    return data
  },

  async generate(
    projectId: number,
    body: ProjectReportCreate
  ): Promise<ProjectReportItem> {
    const { data } = await apiClient.post<ProjectReportItem>(
      `/api/v1/projects/${projectId}/reports/`,
      body
    )
    return data
  },

  async get(projectId: number, reportId: number): Promise<ProjectReportItem> {
    const { data } = await apiClient.get<ProjectReportItem>(
      `/api/v1/projects/${projectId}/reports/${reportId}`
    )
    return data
  },

  async exportReport(
    projectId: number,
    reportId: number,
    format: string = 'csv'
  ): Promise<ProjectReportExport> {
    const { data } = await apiClient.get<ProjectReportExport>(
      `/api/v1/projects/${projectId}/reports/${reportId}/export`,
      { params: { format } }
    )
    return data
  },

  /** Request export as blob (for CSV download). Returns blob and suggested filename. */
  async exportReportBlob(
    projectId: number,
    reportId: number,
    format: 'csv' | 'json' = 'csv'
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await apiClient.get(
      `/api/v1/projects/${projectId}/reports/${reportId}/export`,
      { params: { format }, responseType: 'blob' }
    )
    const blob = response.data as Blob
    const disposition = response.headers['content-disposition']
    let filename = `report-${reportId}.${format}`
    if (disposition) {
      const match = /filename="?([^";\n]+)"?/.exec(disposition)
      if (match) filename = match[1].trim()
    }
    return { blob, filename }
  },
}
